import React from "react";
import { Link } from "react-router-dom";
import { useSharedHomepageData } from "../../hooks/queries/useSharedHomepageData";
import { homepageApiService } from "../../services/homepageApiService";
import { LoadingSkeleton } from "../ui/LoadingStates";
import { ErrorMessage } from "../ui/ErrorStates";
import OptimizedContentImage from "../common/OptimizedContentImage";

interface FeaturedGalleryWidgetContentProps {
  count?: number;
  showFeaturedOnly?: boolean;
}

/**
 * FeaturedGalleryWidgetContent - Content-only version without container styling
 * Used by HomePage variations to avoid styling conflicts
 */
const FeaturedGalleryWidgetContent: React.FC<
  FeaturedGalleryWidgetContentProps
> = ({ count = 1, showFeaturedOnly = true }) => {
  const {
    data: homepageData,
    isLoading,
    error,
    refetch,
  } = useSharedHomepageData();

  if (isLoading) {
    return (
      <>
        <div className="flex items-center justify-between mb-6">
          <div>
            <LoadingSkeleton height="h-8" width="w-48" className="mb-2" />
            <LoadingSkeleton height="h-4" width="w-32" />
          </div>
          <LoadingSkeleton height="h-4" width="w-20" />
        </div>

        <div className="bg-gray-800/50 rounded-lg overflow-hidden border border-gray-700">
          <LoadingSkeleton height="h-64" width="w-full" />
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <LoadingSkeleton height="h-6" width="w-48" />
              <LoadingSkeleton height="h-5" width="w-16" />
            </div>
            <LoadingSkeleton height="h-4" width="w-full" />
            <LoadingSkeleton height="h-4" width="w-3/4" />
            <div className="flex items-center justify-between">
              <LoadingSkeleton height="h-4" width="w-24" />
              <LoadingSkeleton height="h-4" width="w-32" />
            </div>
            <LoadingSkeleton height="h-4" width="w-28" />
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        type="network"
        title="Failed to Load Gallery"
        message="Unable to load featured gallery. Please try again."
        onRetry={() => refetch()}
        showRetry={true}
        className="text-white"
      />
    );
  }

  const galleries = homepageData?.galleries || [];

  // Filter galleries based on widget parameters
  let filteredGalleries = galleries;
  if (showFeaturedOnly) {
    filteredGalleries = galleries.filter((gallery) => gallery.featured);
  }
  // Limit to requested count
  filteredGalleries = filteredGalleries.slice(0, count);

  if (filteredGalleries.length === 0) {
    return null; // Hide widget completely when no galleries
  }

  // Show the first (most recent) gallery
  const featuredGallery = filteredGalleries[0];

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">
            Featured Gallery
          </h2>
          <p className="text-slate-200 text-sm sm:text-base mt-1">
            Latest photo collection
          </p>
        </div>
        <Link
          to="/galleries"
          className="text-red-500 hover:text-red-400 transition-colors text-sm font-medium"
        >
          View All →
        </Link>
      </div>

      <GalleryCard gallery={featuredGallery} />
    </>
  );
};

interface GalleryCardProps {
  gallery: {
    id: string;
    title: string;
    slug: string;
    description: string;
    cover_image: string;
    photo_count: number;
    featured: boolean;
    created_at: string;
    updated_at: string;
  };
}

const GalleryCard: React.FC<GalleryCardProps> = ({ gallery }) => {
  const imageUrl = gallery.cover_image
    ? homepageApiService.getImageUrl(gallery.cover_image, "gallery")
    : null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <Link to={`/galleries/${gallery.slug}`} className="group block">
      <div className="bg-gray-800/50 rounded-lg overflow-hidden border border-gray-700 hover:border-red-500/50 transition-all duration-300 group-hover:transform group-hover:scale-[1.02]">
        {imageUrl && (
          <div className="aspect-video overflow-hidden">
            <OptimizedContentImage
              src={imageUrl}
              alt={gallery.title}
              className="w-full h-full"
              imageClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        )}

        <div className="p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-semibold text-white group-hover:text-red-400 transition-colors">
              {gallery.title}
            </h3>
            {gallery.featured && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                Featured
              </span>
            )}
          </div>

          {gallery.description && (
            <p className="text-slate-200 text-sm mb-4 line-clamp-2">
              {gallery.description}
            </p>
          )}

          <div className="flex items-center justify-between text-sm text-slate-200">
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>
                {gallery.photo_count} photo
                {gallery.photo_count !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>{formatDate(gallery.created_at)}</span>
            </div>
          </div>

          <div className="mt-4 flex items-center text-red-400 text-sm font-medium">
            <span>View Gallery</span>
            <svg
              className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default FeaturedGalleryWidgetContent;
